import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import superAdminAuthService from '../services/superAdminAuth.service';
import {
  SuperAdminAuthContextType,
  SuperAdminLoginData,
  SuperAdminSetPasswordData,
  SuperAdminUser,
} from '../types/superAdmin.types';

const SuperAdminAuthContext = createContext<SuperAdminAuthContextType | undefined>(undefined);

interface SuperAdminAuthProviderProps {
  children: ReactNode;
}

export const SuperAdminAuthProvider: React.FC<SuperAdminAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<SuperAdminUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeAuth = () => {
      const tokens = superAdminAuthService.getTokens();
      const savedUser = superAdminAuthService.getUser();

      if (tokens.accessToken && tokens.refreshToken && savedUser) {
        setAccessToken(tokens.accessToken);
        setRefreshToken(tokens.refreshToken);
        setUser(savedUser);
      }

      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const applyAuthPayload = (nextUser: SuperAdminUser, nextAccessToken: string, nextRefreshToken: string) => {
    setUser(nextUser);
    setAccessToken(nextAccessToken);
    setRefreshToken(nextRefreshToken);
    superAdminAuthService.saveTokens(nextAccessToken, nextRefreshToken);
    superAdminAuthService.saveUser(nextUser);
  };

  const login = async (data: SuperAdminLoginData): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await superAdminAuthService.login(data);
      if (response.success && response.data?.user && response.data.accessToken && response.data.refreshToken) {
        applyAuthPayload(response.data.user, response.data.accessToken, response.data.refreshToken);
        return;
      }

      throw new Error(response.error?.message || 'Connexion impossible');
    } catch (err: any) {
      const raw = err.response?.data?.error?.message || err.message || 'Connexion impossible';
      const errorMessage = raw === 'Invalid email or password' ? 'E-mail ou mot de passe incorrect' : raw;
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const setPassword = async (data: SuperAdminSetPasswordData): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await superAdminAuthService.setPassword(data);
      if (response.success && response.data?.user && response.data.accessToken && response.data.refreshToken) {
        applyAuthPayload(response.data.user, response.data.accessToken, response.data.refreshToken);
        return;
      }

      throw new Error(response.error?.message || 'Password setup failed');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || err.message || 'Password setup failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    setUser(null);
    setAccessToken(null);
    setRefreshToken(null);
    setError(null);
    superAdminAuthService.clearAuthData();
    try {
      await superAdminAuthService.logout();
    } catch {
      // non-blocking
    }
  };

  const refreshAccessToken = async (): Promise<void> => {
    const stored = superAdminAuthService.getTokens();
    const refreshTokenToUse = stored.refreshToken ?? refreshToken;
    if (!refreshTokenToUse) {
      await logout();
      throw new Error('No refresh token available');
    }

    try {
      const { accessToken: nextAccessToken, refreshToken: nextRefreshToken } =
        await superAdminAuthService.refreshToken(refreshTokenToUse);
      setAccessToken(nextAccessToken);
      setRefreshToken(nextRefreshToken);
      superAdminAuthService.saveTokens(nextAccessToken, nextRefreshToken);
    } catch (err) {
      await logout();
      throw err;
    }
  };

  const refreshProfile = async (): Promise<void> => {
    const data = await superAdminAuthService.getCurrentUser();
    setUser(data.user);
    superAdminAuthService.saveUser(data.user);
  };

  const clearError = (): void => {
    setError(null);
  };

  const value: SuperAdminAuthContextType = {
    user,
    accessToken,
    refreshToken,
    isAuthenticated: !!user && !!accessToken,
    isLoading,
    error,
    login,
    logout,
    setPassword,
    refreshAccessToken,
    refreshProfile,
    clearError,
  };

  return <SuperAdminAuthContext.Provider value={value}>{children}</SuperAdminAuthContext.Provider>;
};

export const useSuperAdminAuth = (): SuperAdminAuthContextType => {
  const context = useContext(SuperAdminAuthContext);
  if (!context) {
    throw new Error('useSuperAdminAuth must be used within a SuperAdminAuthProvider');
  }
  return context;
};

export default SuperAdminAuthContext;
